import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { getStaffToken, hasPermission } from "@/lib/api";
import { subscribeNotification } from "@/lib/notification-ws";
import { useStaffConnectionKey } from "@/lib/staffConnection";
import { useNotificationContext } from "@/contexts/useNotificationContext";
import { isPromptScreen } from "@/components/qr-orders/qrAcceptPromptState";
import {
  fetchPendingPrintRequests,
  itemsFromEvent,
  printForOthers,
  printsForOthers,
  type PrintRequestEventMeta,
  type PrintRequestItem,
} from "@/lib/remotePrint";

/** A till re-reads what is pending this often (a request whose event it missed). */
export const PENDING_POLL_MS = 45_000;
import type { WsNotificationEvent } from "@/types/notification";

/** Tills and floor staff take print requests (the server's claim permission). */
function canPrintForOthers(): boolean {
  return hasPermission("orders.edit") || hasPermission("orders.settle");
}

/**
 * Prints, on this device, the KOTs and bills other devices could not print
 * (Printer Configuration › Print for other devices). Renders nothing.
 */
export function RemotePrintAgent() {
  const { pathname } = useLocation();
  const connectionKey = useStaffConnectionKey();
  if (!connectionKey || !getStaffToken() || !canPrintForOthers() || !isPromptScreen(pathname)) return null;
  return <RemotePrintAgentHost key={connectionKey} />;
}

function RemotePrintAgentHost() {
  const { wsStatus } = useNotificationContext();
  // One batch at a time: a claim per request, in the order they came.
  const chain = useRef<Promise<void>>(Promise.resolve());

  const run = useCallback((items: () => Promise<PrintRequestItem[]>) => {
    chain.current = chain.current.then(async () => {
      try {
        const printed = await printForOthers(await items());
        for (const p of printed) toast({ title: p.message });
      } catch (error) {
        console.warn("Remote printing failed", error);
      }
    });
  }, []);

  // A print request from another device.
  useEffect(
    () =>
      subscribeNotification("print_request", (payload) => {
        const meta = (payload as WsNotificationEvent).metadata as Partial<PrintRequestEventMeta> | null;
        const items = itemsFromEvent(meta);
        if (items.length > 0) run(async () => items);
      }),
    [run],
  );

  // What is still waiting for a till: on load, on every reconnect, and every
  // 45 s. Only while this device prints for others (paired, a switch on):
  // otherwise nothing is asked.
  const fetchPending = useCallback(() => {
    if (printsForOthers()) run(fetchPendingPrintRequests);
  }, [run]);
  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    if (wsStatus === "connected" && lastStatus.current !== "connected") fetchPending();
    lastStatus.current = wsStatus;
  }, [wsStatus, fetchPending]);
  useEffect(() => {
    const timer = setInterval(fetchPending, PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [fetchPending]);

  return null;
}
